/** Fusion AI (the personal assistant): API types, query keys and small helpers shared by the panel and the full page. */
import type { QueryClient } from "@tanstack/react-query";

export interface AsConversation { id: string; title: string; projectId: string | null; day: string | null; kind: string; createdAt: string; updatedAt: string }
export interface AsProject { id: string; name: string; persona: { name: string; instructions: string } | null; oneProjectId?: string | null }
export interface AsState { conversations: AsConversation[]; projects: AsProject[]; rules: unknown; model: string }
export interface AsAction { tool: string; args: Record<string, unknown>; summary: string }
export interface AsApplied { at?: string; ok?: boolean; error?: string; dismissed?: boolean; result?: unknown }
export interface AsFile { documentId: string; name: string; type: string; chars: number }
export interface AsMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  actions: AsAction[] | null;
  applied: Record<string, AsApplied> | null;
  steps: unknown[] | null;
  files: AsFile[] | null;
  meta: { model?: string; usage?: unknown; minutes?: unknown; title?: string; rhythm?: string; quickReplies?: string[]; pending?: boolean; startedAt?: string; error?: boolean } | null;
  createdAt: string;
}
export interface AsThread { conversation: AsConversation; messages: AsMessage[] }
export interface AsSearchHit { conversationId: string; title: string; messageId: string; role: string; snippet: string; at: string }

export type MemoryKind = "preference" | "dont_remind" | "fact";
export interface AsMemoryItem { id: string; kind: MemoryKind; text: string; source: string | null; createdAt: string }
export type RhythmPref = "morning" | "meetings" | "nudges" | "wrap" | "weekly";
export interface AsMemory { memory: AsMemoryItem[]; prefs: Record<RhythmPref, boolean> }

/** meta.rhythm on a proactive message → the small label above it. */
export const RHYTHM_LABEL: Record<string, string> = {
  morning: "Morning brief", meeting: "Before your meeting", meetings: "Before your meeting", nudges: "Worth a look", wrap: "End of day", weekly: "Weekly review",
};

export const isBrief = (c: AsConversation) => c.kind === "brief";

export const AK = {
  state: ["assistant", "state"] as const,
  memory: ["assistant", "memory"] as const,
  thread: (id: string) => ["assistant", "conversation", id] as const,
  search: (q: string) => ["assistant", "search", q] as const,
};

/** Fetch that keeps the body on errors: the action and message endpoints answer 409/422 with useful JSON. */
export async function call<T = Record<string, unknown>>(method: string, path: string, json?: unknown): Promise<{ ok: boolean; status: number; body: T | null }> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: json !== undefined ? { "content-type": "application/json" } : {},
    body: json !== undefined ? JSON.stringify(json) : undefined,
  });
  const text = await res.text();
  let body: T | null = null;
  try { body = text ? (JSON.parse(text) as T) : null; } catch { body = null; }
  return { ok: res.ok, status: res.status, body };
}

/** ONE's { error, message } or a plain { error: "text" }: show message ?? error. */
export function errText(body: unknown, status?: number): string {
  const b = body as { message?: unknown; error?: unknown } | null;
  if (b && typeof b.message === "string" && b.message) return b.message;
  if (b && typeof b.error === "string" && b.error) return b.error;
  return status ? `Something went wrong (${status})` : "Something went wrong";
}

/** After a confirmed action anything outside the assistant may have changed (my-day, tasks, plan, notes, mail). */
export const refreshAfterAction = (qc: QueryClient) => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "assistant" });

export const readFile = (file: File) =>
  new Promise<{ name: string; type: string; contentBase64: string }>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name, type: file.type || "application/octet-stream", contentBase64: String(r.result).split(",")[1] || "" });
    r.onerror = () => reject(new Error(`Could not read ${file.name}`));
    r.readAsDataURL(file);
  });

export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

const FRIENDLY: Record<string, string> = {
  my_day: "your day", list_tasks: "tasks", get_task: "a task", search: "search", knowledge_search: "knowledge", knowledge_ask: "knowledge",
  notes: "notes", calendar: "calendar", mail_list: "mail", mail_thread: "mail", fetch_page: "a web page", wealth_summary: "wealth",
  today_plan: "your plan", plan_suggestion: "your plan",
};
/** message.steps → unique friendly words ("your day", "tasks"…). */
export function lookedAt(steps: unknown[] | null | undefined): string[] {
  const out: string[] = [];
  for (const s of steps ?? []) {
    const tool = typeof s === "string" ? s : s && typeof s === "object" && "tool" in s ? String((s as { tool: unknown }).tool) : "";
    if (!tool) continue;
    const word = FRIENDLY[tool] ?? tool.replace(/_/g, " ");
    if (!out.includes(word)) out.push(word);
  }
  return out;
}

export const sortRecent = (list: AsConversation[]) => [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

const OPEN_KEY = "one-fusion-ai-conversation";
export function loadOpenId(): string | null {
  try { return localStorage.getItem(OPEN_KEY) || null; } catch { return null; }
}
export function saveOpenId(id: string | null) {
  try { if (id) localStorage.setItem(OPEN_KEY, id); else localStorage.removeItem(OPEN_KEY); } catch { /* private window */ }
}
